import {
  UI_ELEMENT_WITHIN_MAX_LENGTH,
  UI_FIELD_MAX_OPTIONS,
  UI_FIELD_OPTION_MAX_LENGTH,
  type UiObjectRef,
  type UiSemanticElement,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { nanoid } from 'nanoid';

import {
  CONTEXT_PICKING_TOGGLE_ATTRIBUTE,
  SURFACE_DELIVERED_CONTENT_ATTRIBUTE,
  VIEW_COVERING_LAYER_ATTRIBUTE,
} from './context-picking-attributes';
import { classifyUiAccess, isSecretUiContent } from './ui-access-rules';
import { viewReadClock } from './view-read-clock';

// What no read describes because the user does not read it here either: markup, hidden and inert
// parts, another document's content, pixels (a drawn surface lists its objects itself), the operating
// system's file choice and the AI's own cursor. No access rule lives here: the four categories are
// the central list's, secret content (category 4) included.
const unread = [
  'script',
  'style',
  'template',
  'noscript',
  'iframe',
  'canvas',
  'input[type="hidden" i]',
  'input[type="file" i]',
  '[hidden]',
  '[inert]',
  '[aria-hidden="true"]',
  // Rich text: documents and drawn surfaces deliver theirs; other editors are not read yet.
  '[contenteditable]:not([contenteditable="false" i])',
  '[role="textbox"]',
  '[data-ai-cursor]',
];
// Automatic reads never describe Copilot surfaces back to Copilot, and never spend their walk on
// markup a drawn surface delivers itself.
const excluded = [
  ...unread,
  '[data-copilot-ignore]',
  `[${SURFACE_DELIVERED_CONTENT_ATTRIBUTE}]`,
].join(',');
// An explicit pick never targets Copilot itself, nor its selector toggle.
const pickingExcluded = [
  ...unread,
  '[data-copilot-ignore]',
  `[${CONTEXT_PICKING_TOGGLE_ATTRIBUTE}]`,
].join(',');
// A drawn surface delivers what it draws itself, so what keeps a read from walking into it
// (rich text, pixels) does not keep it out: only what takes it away from the user does.
export const withdrawn = ['[hidden]', '[inert]', '[aria-hidden="true"]'].join(
  ','
);
/** Automatic projection of the surface, an explicit user pick, or a drawn surface as a whole. */
type SemanticAdmission = 'automatic' | 'explicit' | 'surface';
// Read references only: these keys never authorize a click or identify a domain object.
const localKeys = new WeakMap<Element, string>();
const normalize = (text: string) => text.replace(/\s+/gu, ' ').trim();
/**
 * The key an element of the page is addressed by. It names the element only:
 * the workspace, the view and its revision are bound by what carries the key.
 */
function semanticKey(element: Element, nativeId = true) {
  let key =
    (nativeId ? element.id.slice(0, 256) : '') || localKeys.get(element);
  if (!key) {
    key = nanoid();
    localKeys.set(element, key);
  }
  return `dom:${key}`;
}

// The elements behind the keys of recent automatic reads, so an operation can
// address what the AI saw. Bounded; a collected element simply resolves to null.
const OPERABLE_LIMIT = 2048;
const operable = new Map<string, WeakRef<Element>>();
function rememberOperable(key: string, element: Element) {
  operable.delete(key);
  operable.set(key, new WeakRef(element));
  if (operable.size > OPERABLE_LIMIT)
    operable.delete(operable.keys().next().value!);
}

/** The element a recent automatic read listed under this key, if still alive. */
export function resolveSemanticDomElement(key: string): Element | null {
  return operable.get(key)?.deref() ?? null;
}

/** Whether a recent automatic read listed this element under a key of its own. */
export function isSemanticDomElementListed(element: Element): boolean {
  return [element.id.slice(0, 256), localKeys.get(element)].some(
    key => !!key && operable.get(`dom:${key}`)?.deref() === element
  );
}

type FormField = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
const inputRoles: Record<string, string> = {
  checkbox: 'checkbox',
  radio: 'radio',
  range: 'slider',
  number: 'spinbutton',
  search: 'searchbox',
  button: 'button',
  submit: 'button',
  reset: 'button',
};
function formField(element: Element): FormField | null {
  return /^(?:input|textarea|select)$/u.test(element.localName)
    ? (element as FormField)
    : null;
}
function fieldRole(field: FormField): string {
  if (field.localName === 'select') return 'combobox';
  if (field.localName === 'textarea') return 'textbox';
  return inputRoles[(field as HTMLInputElement).type] ?? 'textbox';
}
/** The value the user sees in the field; toggles report it as `selected`. */
function fieldValue(field: FormField): string {
  if (field.localName === 'select')
    return (field as HTMLSelectElement).selectedOptions[0]?.text ?? '';
  const type = (field as HTMLInputElement).type;
  return type === 'checkbox' || type === 'radio' ? '' : field.value;
}
/** The choices of a field with a fixed list, as the user reads them. */
function fieldOptions(field: FormField): string[] {
  if (field.localName !== 'select') return [];
  const options: string[] = [];
  for (const option of (field as HTMLSelectElement).options) {
    if (options.length >= UI_FIELD_MAX_OPTIONS) break;
    const text = normalize(option.text.slice(0, UI_FIELD_OPTION_MAX_LENGTH));
    if (text && !option.disabled) options.push(text);
  }
  return options;
}
function fieldLabel(field: FormField): string {
  const labels = Array.from(field.labels ?? [])
    .slice(0, 2)
    .map(boundedPickText)
    .join(' ');
  return normalize(
    labels ||
      field.getAttribute('placeholder') ||
      field.getAttribute('name') ||
      ''
  );
}

/** Explicit hit admission visits only the bounded composed ancestor chain:
 * the nearest semantic ancestor of a hit, or null when the hit is not pickable. */
export function resolveSemanticPickTarget(
  element: Element,
  root: HTMLElement,
  scope: WorkspaceViewScope
): Element | null {
  let current: Element | null = element;
  let target: Element | undefined;
  let insideSummary = false;
  for (
    let depth = 0;
    current && depth < 128;
    depth++, current = parent(current)
  ) {
    if (current.localName === 'summary') insideSummary = true;
    const style = current.ownerDocument.defaultView?.getComputedStyle(current);
    if (
      blockedSemanticElement(current, scope, 'explicit') ||
      style?.display === 'none' ||
      style?.visibility === 'hidden' ||
      style?.visibility === 'collapse' ||
      style?.opacity === '0' ||
      (current.localName === 'details' &&
        !current.hasAttribute('open') &&
        element !== current &&
        !insideSummary)
    )
      return null;
    // A hit inside secret content stands for the secret element; nothing within it is picked.
    if (isSecretUiContent(current)) target = current;
    if (
      !target &&
      (formField(current) ||
        current.hasAttribute('role') ||
        current.hasAttribute('aria-label') ||
        current.hasAttribute('title') ||
        /^(?:button|a|summary|label|p|li|dt|dd|th|td|h[1-6])$/u.test(
          current.localName
        ))
    )
      target = current;
    if (current === root) return target ? labelledField(target, scope) : null;
  }
  return null;
}

/** A picked field label stands for the field it labels: the field is what the user edits. */
function labelledField(target: Element, scope: WorkspaceViewScope): Element {
  const field =
    target.localName === 'label' ? (target as HTMLLabelElement).control : null;
  return field && !blockedSemanticElement(field, scope, 'explicit')
    ? field
    : target;
}

const PICK_LABEL = { length: 80, nodes: 64 };

/** Bounded visible text of one admitted element: Text data only, secret content, unread
 * descendants and nested field values skipped, never a full-subtree textContent read. */
function boundedPickText(element: Element): string {
  if (isSecretUiContent(element)) return '';
  const parts: string[] = [];
  const stack: Node[] = [element];
  let length = 0,
    visits = 0;
  while (
    stack.length &&
    visits < PICK_LABEL.nodes &&
    length < PICK_LABEL.length
  ) {
    const node = stack.pop()!;
    visits++;
    if (node instanceof Text) {
      const text = normalize(node.data.slice(0, PICK_LABEL.length + 1));
      if (text) {
        parts.push(text);
        length += text.length + 1;
      }
      continue;
    }
    if (
      !(node instanceof Element) ||
      (node !== element &&
        (node.matches(pickingExcluded) ||
          formField(node) ||
          isSecretUiContent(node)))
    )
      continue;
    const children: Node[] = [];
    for (
      let child = (node.shadowRoot ?? node).firstChild;
      child && children.length < PICK_LABEL.nodes;
      child = child.nextSibling
    )
      children.push(child);
    for (let index = children.length - 1; index >= 0; index--)
      stack.push(children[index]);
  }
  return normalize(parts.join(' '));
}

/** Document or shadow root of the element, from any realm; a detached
 * subtree has no id scope and resolves nothing. */
function labelledByElement(element: Element, id: string): Element | null {
  const root = element.getRootNode();
  return 'getElementById' in root
    ? (root as Node & NonElementParentNode).getElementById(id)
    : null;
}

/** Accessible name from attributes and referenced labels, without own text. */
function attributePickLabel(element: Element): string {
  const labelledBy = (element.getAttribute('aria-labelledby') ?? '')
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 8)
    .map(id => {
      const label = labelledByElement(element, id);
      return label ? boundedPickText(label) : '';
    })
    .join(' ');
  return normalize(
    element.getAttribute('aria-label') ||
      labelledBy ||
      element.getAttribute('alt') ||
      element.getAttribute('title') ||
      ''
  );
}

function semanticRole(element: Element): string | undefined {
  return (
    element.getAttribute('role')?.split(/\s/u)[0] ||
    (/^h[1-6]$/u.test(element.localName)
      ? 'heading'
      : implicitRoles[element.localName])
  );
}

/** Chip label of a pick: its accessible name or visible text, a field's own label; a
 * text-less control is named by its role inside the nearest labelled ancestor. */
function semanticPickLabel(target: Element, root: HTMLElement): string {
  const field = formField(target);
  let label =
    attributePickLabel(target) ||
    (field ? fieldLabel(field) : boundedPickText(target));
  if (!label) {
    const role = semanticRole(target) ?? 'element';
    let context = '';
    for (
      let current = target === root ? null : parent(target), depth = 0;
      current && !context && depth < 128;
      current = current === root ? null : parent(current), depth++
    )
      context = attributePickLabel(current);
    label = context ? `${context} \u203a ${role}` : role;
  }
  return label.length > PICK_LABEL.length
    ? `${label.slice(0, PICK_LABEL.length - 1).trimEnd()}\u2026`
    : label;
}

/** The pick's stable read reference plus its human-readable chip label. */
export function captureSemanticDomRef(
  element: Element,
  root: HTMLElement,
  scope: WorkspaceViewScope
): { element: Element; ref: UiObjectRef; label: string } | null {
  const target = resolveSemanticPickTarget(element, root, scope);
  if (!target) return null;
  return {
    element: target,
    label: semanticPickLabel(target, root),
    ref: {
      kind: 'ui-element',
      id: semanticKey(target, false),
      attributes: {
        workspaceId: scope.workspaceId,
        viewId: scope.viewId,
        tag: target.localName,
      },
    },
  };
}

/** Local event admission only: bounded ancestry and attributes, never layout/body reads. */
export function projectSemanticInteraction(
  element: Element,
  root: HTMLElement,
  scope: WorkspaceViewScope
): { element: Element; semantic: UiSemanticElement } | null {
  let target: Element | undefined;
  let insideSummary = false;
  for (
    let current: Element | null = element, depth = 0;
    current && depth < DOM_LIMIT.depth;
    current = parent(current), depth++
  ) {
    if (current.localName === 'summary') insideSummary = true;
    if (
      blockedSemanticElement(current, scope) ||
      (current.localName === 'details' &&
        !current.hasAttribute('open') &&
        !insideSummary &&
        element !== current)
    )
      return null;
    if (isSecretUiContent(current)) target = current;
    if (
      !target &&
      (current.hasAttribute('role') ||
        current.hasAttribute('aria-label') ||
        current.hasAttribute('aria-labelledby') ||
        current.hasAttribute('title') ||
        implicitRoles[current.localName] ||
        /^h[1-6]$/u.test(current.localName))
    )
      target = current;
    if (current === root) {
      if (!target) return null;
      return {
        element: target,
        semantic: {
          key: semanticKey(target),
          role: implicitRoles[target.localName] ?? 'element',
          label: target.localName,
          text: '',
          selected: false,
          disabled: false,
          refs: [],
        },
      };
    }
  }
  return null;
}

function blockedSemanticElement(
  element: Element,
  scope: WorkspaceViewScope,
  admission: SemanticAdmission = 'automatic'
): boolean {
  const viewId = element.getAttribute('data-workspace-view-id');
  const workspaceId = element.getAttribute('data-workspace-id');
  return (
    !element.isConnected ||
    (admission === 'explicit'
      ? element.matches(pickingExcluded)
      : admission === 'surface'
        ? element.matches(withdrawn)
        : element.matches(excluded) ||
          /ai-chat|^chat-/iu.test(element.localName)) ||
    !!(viewId && viewId !== scope.viewId) ||
    !!(workspaceId && workspaceId !== scope.workspaceId)
  );
}

function parent(element: Element): Element | null {
  return (
    element.parentElement ??
    (element.getRootNode() instanceof ShadowRoot
      ? (element.getRootNode() as ShadowRoot).host
      : null)
  );
}
/** The layer that lies over a view region now (a fullscreen view); null while none does. */
export function coveringLayer(region: Element): Element | null {
  const layer = Array.from(
    region.querySelectorAll(`[${VIEW_COVERING_LAYER_ATTRIBUTE}]`)
  ).at(-1);
  if (!layer) return null;
  const bounds = layer.getBoundingClientRect();
  return bounds.width > 0 && bounds.height > 0 ? layer : null;
}

/** Whether a covering layer of the element's view region lies over it: the user does not see
 * the element then, so no read lists it. */
export function isUnderCoveringLayer(element: Element): boolean {
  let region: Element | null = element;
  while (region && !region.hasAttribute('data-workspace-view-id'))
    region = parent(region);
  const layer = region && coveringLayer(region);
  if (!layer) return false;
  for (
    let current: Element | null = element;
    current;
    current = parent(current)
  )
    if (current === layer) return false;
  return true;
}

export function isSemanticDomElementVisible(
  element: Element,
  scope: WorkspaceViewScope
): boolean {
  return new SemanticDomReader(scope).visible(element);
}

/** Visibility of an operation target over its whole ancestor chain: an element
 * the bounded read listed may sit deeper in the document than a read walks. */
export function isSemanticDomElementOperable(
  element: Element,
  scope: WorkspaceViewScope
): boolean {
  return new SemanticDomReader(scope).visible(element, Infinity);
}

/**
 * Visibility of a drawn surface an operation goes to, over its whole ancestor chain. The surface
 * is operated as a whole, whatever it is made of: a page of a document is one editable region.
 */
export function isDrawnSurfaceOperable(
  root: Element,
  scope: WorkspaceViewScope
): boolean {
  return new SemanticDomReader(scope).visible(root, Infinity, 'surface');
}

/** Rendering/admission without viewport clipping, for scrolling an admitted target. */
export function isSemanticDomElementRendered(
  element: Element,
  scope: WorkspaceViewScope
): boolean {
  return new SemanticDomReader(scope).rendered(element);
}

const DOM_LIMIT = {
  /** Elements one read examines: each costs a style and a geometry read. */
  nodes: 256,
  /** Every node one read steps over, template markers and blank text included. */
  steps: 2048,
  /** How deep below its region a read goes. A table in a block of a canvas keeps its controls
   * more than forty elements deep; the elements and steps above bound the work. */
  depth: 64,
  text: 4096,
  output: 16384,
  label: 256,
  taskNodes: 32,
};
/**
 * The sides on which a scroll area holds more than it shows, in the words a read carries
 * ("more content below"). Empty where all of it shows, and where the user's wheel moves nothing.
 */
function contentBeyond(element: Element, style: CSSStyleDeclaration): string {
  const moves = (overflow: string) =>
    /^(?:auto|scroll|overlay)$/u.test(overflow);
  const sides: string[] = [];
  if (moves(style.overflowY)) {
    const before = element.scrollTop;
    if (before > 1) sides.push('above');
    if (element.scrollHeight - element.clientHeight - before > 1)
      sides.push('below');
  }
  if (moves(style.overflowX)) {
    // A right-to-left area starts on the right and counts its position down from zero.
    const before = Math.abs(element.scrollLeft);
    const after = element.scrollWidth - element.clientWidth - before;
    const rtl = style.direction === 'rtl';
    if ((rtl ? after : before) > 1) sides.push('left');
    if ((rtl ? before : after) > 1) sides.push('right');
  }
  return sides.length ? `more content ${sides.join(', ')}` : '';
}
const implicitRoles: Record<string, string> = {
  button: 'button',
  a: 'link',
  label: 'label',
  p: 'paragraph',
  li: 'listitem',
  dt: 'term',
  dd: 'definition',
  th: 'columnheader',
  td: 'cell',
  summary: 'button',
  caption: 'caption',
};
// A graphic without text, names or roles holds nothing a read lists: its shapes are not walked.
const NAMED_IN_GRAPHIC =
  'text, title, desc, foreignObject, [role], [aria-label], [aria-labelledby], [title]';
const isBareGraphic = (element: Element) =>
  element.localName === 'svg' && !element.querySelector(NAMED_IN_GRAPHIC);
type Clip = { left: number; right: number; top: number; bottom: number };
type DomEntry = {
  element: Element;
  /** The nearest named or role-bearing element this one lies in. */
  owner?: DomEntry;
  role?: string;
  text: string;
  directText: boolean;
  labelledBy: string;
  explicitLabel: string;
  title: string;
  options: string[];
  localRoot: Node;
  /** The sides a scroll area goes on to beyond what it shows; empty for all else. */
  beyond: string;
};
type DomFrame = {
  node: Node;
  depth: number;
  owner?: DomEntry;
  container?: DomEntry;
  clip: Clip;
  closed: boolean;
  sibling: boolean;
};

/** One bounded traversal. Text and geometry are read only after admission; no
 * descendant projection pass, full-tree text getter or per-node ancestor walk.
 * Browser CSS/layout can still cause reflow: this is an owner-work bound only. */
class SemanticDomReader {
  constructor(
    private readonly scope: WorkspaceViewScope,
    private readonly admission: SemanticAdmission = 'automatic'
  ) {}
  private blocked(element: Element, admission = this.admission): boolean {
    return blockedSemanticElement(element, this.scope, admission);
  }
  /** `own` admits the element itself; what it sits in is admitted like in every read. */
  rendered(
    element: Element,
    maxDepth: number = DOM_LIMIT.depth,
    own: SemanticAdmission = this.admission
  ): boolean {
    let summary = false;
    for (
      let current: Element | null = element, depth = 0;
      current;
      current = parent(current), depth++
    ) {
      if (
        depth >= maxDepth ||
        this.blocked(current, current === element ? own : this.admission) ||
        // Nothing inside secret content is rendered to a read.
        (current !== element && isSecretUiContent(current))
      )
        return false;
      if (current.localName === 'summary') summary = true;
      const style =
        current.ownerDocument.defaultView?.getComputedStyle(current);
      if (
        !style ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        style.opacity === '0' ||
        (current.localName === 'details' &&
          !current.hasAttribute('open') &&
          current !== element &&
          !summary)
      )
        return false;
    }
    return true;
  }
  visible(
    element: Element,
    maxDepth: number = DOM_LIMIT.depth,
    own: SemanticAdmission = this.admission
  ): boolean {
    if (!this.rendered(element, maxDepth, own)) return false;
    const window = element.ownerDocument.defaultView!;
    const bounds = element.getBoundingClientRect();
    return (
      bounds.width > 0 &&
      bounds.height > 0 &&
      bounds.right > 0 &&
      bounds.bottom > 0 &&
      bounds.left < window.innerWidth &&
      bounds.top < window.innerHeight
    );
  }
  /** A projection passes its target as `start`: the walk then covers only that
   * subtree, while rendering and clipping are still admitted from `root`. */
  *scan(
    root: Element,
    deadline = viewReadClock.now() + 50,
    observeShadow?: (shadow: ShadowRoot) => void,
    start: Element = root
  ): Generator<void, UiSemanticElement[], void> {
    if (!this.rendered(root)) return [];
    const window = root.ownerDocument.defaultView!;
    const viewport: Clip = {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
    // The bound root may sit inside a scroll/clip container. Read that one bounded
    // ancestor chain once, then carry its intersection through the traversal.
    for (
      let ancestor = parent(root), depth = 0;
      ancestor && depth < DOM_LIMIT.depth;
      ancestor = parent(ancestor), depth++
    ) {
      const style = window.getComputedStyle(ancestor);
      const clipX = /(?:auto|scroll|hidden|clip)/u.test(
        style.overflowX || style.overflow
      );
      const clipY = /(?:auto|scroll|hidden|clip)/u.test(
        style.overflowY || style.overflow
      );
      if (!clipX && !clipY) continue;
      const box = ancestor.getBoundingClientRect();
      if (clipX) {
        viewport.left = Math.max(viewport.left, box.left);
        viewport.right = Math.min(viewport.right, box.right);
      }
      if (clipY) {
        viewport.top = Math.max(viewport.top, box.top);
        viewport.bottom = Math.min(viewport.bottom, box.bottom);
      }
    }
    const stack: DomFrame[] = [
      { node: start, depth: 0, clip: viewport, closed: false, sibling: false },
    ];
    const entries: DomEntry[] = [];
    const ids = new Map<Node, Map<string, DomEntry>>();
    let visits = 0,
      steps = 0,
      rawText = 0;
    while (
      stack.length &&
      visits < DOM_LIMIT.nodes &&
      steps < DOM_LIMIT.steps &&
      viewReadClock.now() < deadline
    ) {
      const frame = stack.pop()!;
      // Admission precedes nextSibling/firstChild, style, geometry and text reads.
      if (frame.depth >= DOM_LIMIT.depth) continue;
      steps++;
      const { node } = frame;
      // A rendered component is mostly template markers and blank text between its
      // elements; they cost one step and leave the element budget to the controls.
      const examined = node.nodeType === Node.ELEMENT_NODE;
      if (examined) visits++;
      if (frame.sibling && visits < DOM_LIMIT.nodes) {
        const next = node.nextSibling;
        if (next) stack.push({ ...frame, node: next });
      }
      if (node.nodeType === Node.TEXT_NODE) {
        if (rawText < DOM_LIMIT.text && frame.container) {
          const textNode = node as Text;
          const length = Math.min(
            textNode.length,
            DOM_LIMIT.label,
            DOM_LIMIT.text - rawText
          );
          rawText += length;
          const text = normalize(textNode.substringData(0, length));
          const append = (entry: DomEntry) => {
            entry.text = (entry.text + ' ' + text).slice(0, DOM_LIMIT.label);
          };
          if (text) {
            frame.container.directText = true;
            append(frame.container);
            if (frame.owner && frame.owner !== frame.container)
              append(frame.owner);
          }
        }
      } else if (
        node instanceof Element &&
        !this.blocked(node) &&
        (!frame.closed || node.localName === 'summary')
      ) {
        const style = window.getComputedStyle(node);
        if (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.visibility !== 'collapse' &&
          style.opacity !== '0'
        ) {
          const bounds = node.getBoundingClientRect();
          const visible =
            bounds.width > 0 &&
            bounds.height > 0 &&
            bounds.right > frame.clip.left &&
            bounds.left < frame.clip.right &&
            bounds.bottom > frame.clip.top &&
            bounds.top < frame.clip.bottom;
          const field = formField(node);
          // Category 4: the element is listed by its label; its value and content are never read.
          const secret = isSecretUiContent(node);
          const role =
            node
              .getAttribute('role')
              ?.slice(0, DOM_LIMIT.label)
              .split(/\s/u)[0] ||
            (field
              ? fieldRole(field)
              : /^h[1-6]$/u.test(node.localName)
                ? 'heading'
                : implicitRoles[node.localName]);
          const value =
            field && !secret
              ? normalize(fieldValue(field).slice(0, DOM_LIMIT.label))
              : '';
          const entry: DomEntry = {
            element: node,
            role,
            text: value,
            directText: !!value,
            labelledBy: (node.getAttribute('aria-labelledby') || '').slice(
              0,
              DOM_LIMIT.label
            ),
            explicitLabel: (
              node.getAttribute('aria-label') ||
              (field ? fieldLabel(field) : '')
            ).slice(0, DOM_LIMIT.label),
            title: (node.getAttribute('title') || '').slice(0, DOM_LIMIT.label),
            options: field && !secret ? fieldOptions(field) : [],
            localRoot: node.getRootNode(),
            owner: frame.owner,
            // A field's value is its content, and a pick names one element, not what to do next.
            beyond:
              visible && !field && !secret && this.admission === 'automatic'
                ? contentBeyond(node, style)
                : '',
          };
          if (visible) {
            entries.push(entry);
            const id = node.id.slice(0, DOM_LIMIT.label);
            if (id) {
              let index = ids.get(entry.localRoot);
              if (!index) ids.set(entry.localRoot, (index = new Map()));
              if (!index.has(id)) index.set(id, entry);
            }
          }
          const owner =
            visible && (role || entry.explicitLabel || entry.labelledBy)
              ? entry
              : frame.owner;
          const clip = { ...frame.clip };
          if (
            /(?:auto|scroll|hidden|clip)/u.test(
              style.overflowX || style.overflow
            )
          ) {
            clip.left = Math.max(clip.left, bounds.left);
            clip.right = Math.min(clip.right, bounds.right);
          }
          if (
            /(?:auto|scroll|hidden|clip)/u.test(
              style.overflowY || style.overflow
            )
          ) {
            clip.top = Math.max(clip.top, bounds.top);
            clip.bottom = Math.min(clip.bottom, bounds.bottom);
          }
          // A field's own value stands for its content (e.g. textarea text).
          if (
            !field &&
            !secret &&
            !isBareGraphic(node) &&
            frame.depth + 1 < DOM_LIMIT.depth &&
            visits < DOM_LIMIT.nodes &&
            rawText < DOM_LIMIT.text
          ) {
            const childFrame = {
              depth: frame.depth + 1,
              clip,
              owner,
              container: visible ? entry : undefined,
              closed:
                node.localName === 'details' && !node.hasAttribute('open'),
              sibling: true,
            };
            const shadowRoot = node.shadowRoot;
            if (shadowRoot) observeShadow?.(shadowRoot);
            const shadow = shadowRoot?.firstChild;
            if (shadow) stack.push({ ...childFrame, node: shadow });
            const first = node.firstChild;
            if (first) stack.push({ ...childFrame, node: first });
          }
        }
      }
      if (examined && visits % DOM_LIMIT.taskNodes === 0) yield;
    }
    const result: UiSemanticElement[] = [];
    const keys = new Set<string>();
    let outputBytes = 2;
    for (const entry of entries) {
      // A scroll area that goes on is listed for that alone: the view ends at the screen edge,
      // and only the area says that there is more to scroll to.
      if (
        !entry.role &&
        !entry.directText &&
        !entry.explicitLabel &&
        !entry.labelledBy &&
        !entry.beyond
      )
        continue;
      const labels = entry.labelledBy
        .split(/\s+/u)
        .slice(0, 8)
        .map(
          id =>
            ids.get(entry.localRoot)?.get(id)?.text ||
            (start === root ? '' : this.projectedLabel(entry.element, id))
        )
        .join(' ')
        .slice(0, DOM_LIMIT.label);
      const label = normalize(
        entry.explicitLabel || labels || entry.title || entry.text
      );
      const own = normalize(entry.text);
      if (!label && !own && !entry.beyond) continue;
      const text = entry.beyond
        ? own
          ? `${own} (${entry.beyond})`
          : entry.beyond
        : own;
      // Text that only repeats the name of the control it lies in says nothing more.
      if (
        !entry.role &&
        !entry.explicitLabel &&
        !entry.labelledBy &&
        !entry.beyond &&
        entry.owner &&
        own ===
          normalize(
            entry.owner.explicitLabel || entry.owner.title || entry.owner.text
          )
      )
        continue;
      const element = entry.element;
      const key = semanticKey(element);
      // A duplicated native id names no single element.
      if (keys.has(key)) continue;
      const access =
        this.admission === 'automatic' ? classifyUiAccess(element) : undefined;
      const item: UiSemanticElement = {
        key,
        role: entry.role || (entry.beyond ? 'scrollable' : 'text'),
        label,
        text,
        selected:
          (element.hasAttribute('aria-current') &&
            element.getAttribute('aria-current') !== 'false') ||
          element.getAttribute('data-active') === 'true' ||
          element.getAttribute('aria-selected') === 'true' ||
          element.getAttribute('aria-checked') === 'true' ||
          (element.localName === 'input' &&
            (element as HTMLInputElement).checked) ||
          ['active', 'selected', 'checked'].includes(
            element.getAttribute('data-state') ?? ''
          ),
        disabled:
          element.hasAttribute('disabled') ||
          element.hasAttribute('data-disabled') ||
          element.getAttribute('aria-disabled') === 'true',
        refs: [],
        ...(access ? { access } : {}),
        ...(entry.options.length ? { options: entry.options } : {}),
      };
      const bytes = new TextEncoder().encode(JSON.stringify(item)).length + 1;
      if (outputBytes + bytes > DOM_LIMIT.output) break;
      outputBytes += bytes;
      keys.add(key);
      if (this.admission === 'automatic') rememberOperable(key, element);
      result.push(item);
    }
    return result;
  }
  /** A label outside a projected subtree is read only when it is rendered itself. */
  private projectedLabel(element: Element, id: string): string {
    const label = labelledByElement(element, id);
    return label && this.rendered(label) ? boundedPickText(label) : '';
  }
  walk(
    root: Element,
    start: Element = root,
    deadline?: number
  ): UiSemanticElement[] {
    const scan = this.scan(root, deadline, undefined, start);
    let step = scan.next();
    while (!step.done) step = scan.next();
    return step.value;
  }
  /** The element first, then what it contains, as far as one read reaches. */
  subtree(element: Element): UiSemanticElement[] {
    for (
      let ancestor = parent(element), depth = 0;
      ancestor && depth < 128;
      ancestor = parent(ancestor), depth++
    )
      if (isSecretUiContent(ancestor)) return [];
    const root =
      element.closest('[data-workspace-view-id]') ||
      element.parentElement ||
      element;
    const key = semanticKey(element);
    const listed = this.walk(root, element);
    const own = listed.find(entry => entry.key === key);
    return own ? [own, ...listed.filter(entry => entry !== own)] : [];
  }
  project(element: Element): UiSemanticElement | null {
    return this.subtree(element)[0] ?? null;
  }
}

/** Supplementary request work yields by task; a stale binding discards all output. */
export async function collectSemanticDom(
  root: HTMLElement,
  scope: WorkspaceViewScope,
  options: {
    signal: AbortSignal;
    isCurrent: () => boolean;
    yieldTask: () => Promise<void>;
    deadlineMs?: number;
    observeShadow?: (shadow: ShadowRoot) => void;
  }
): Promise<readonly UiSemanticElement[]> {
  const admitted = () =>
    !options.signal.aborted && root.isConnected && options.isCurrent();
  if (!admitted()) return [];
  const deadline =
    viewReadClock.now() + Math.min(100, Math.max(0, options.deadlineMs ?? 50));
  // Under a covering layer the region is that layer: the walk starts there.
  const scan = new SemanticDomReader(scope).scan(
    root,
    deadline,
    options.observeShadow,
    coveringLayer(root) ?? root
  );
  while (admitted() && viewReadClock.now() < deadline) {
    const step = scan.next();
    if (step.done) return admitted() ? step.value : [];
    const resumed = await new Promise<boolean>((resolve, reject) => {
      let finished = false;
      const finish = (value: boolean, error?: unknown) => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        options.signal.removeEventListener('abort', abort);
        if (error !== undefined) reject(error);
        else resolve(value);
      };
      const abort = () => finish(false);
      const timer = setTimeout(
        () => finish(false),
        Math.max(0, deadline - viewReadClock.now())
      );
      options.signal.addEventListener('abort', abort);
      if (options.signal.aborted) {
        finish(false);
        return;
      }
      try {
        options.yieldTask().then(
          () => finish(true),
          error => finish(false, error)
        );
      } catch (error) {
        finish(false, error);
      }
    });
    if (!resumed) return [];
    // Scope/revision and cancellation are rechecked before touching another node.
    if (!admitted()) return [];
  }
  return [];
}

export function readSemanticDom(
  root: HTMLElement,
  scope: WorkspaceViewScope
): readonly UiSemanticElement[] {
  return new SemanticDomReader(scope).walk(root, coveringLayer(root) ?? root);
}
/**
 * What an object of a drawn surface shows as elements of the page (the views and cells of a
 * table in a block), as an automatic read lists them. The walk starts at the object, so it
 * reaches them wherever the object stands among all the surface draws; rendering and clipping
 * are admitted from the surface.
 */
export function readSemanticDomWithin(
  surface: Element,
  object: Element,
  scope: WorkspaceViewScope,
  deadlineMs: number
): readonly UiSemanticElement[] {
  return new SemanticDomReader(scope).walk(
    surface,
    object,
    viewReadClock.now() + Math.min(100, Math.max(0, deadlineMs))
  );
}
/** The element as an automatic read lists it now, for re-checking an operation target. */
export function readSemanticDomElement(
  element: Element,
  scope: WorkspaceViewScope
): UiSemanticElement | null {
  return new SemanticDomReader(scope).project(element);
}

/** Read-side projection of an explicit pick, admitted like the pick itself. */
export function projectSemanticElement(
  element: Element,
  scope: WorkspaceViewScope
): UiSemanticElement | null {
  return new SemanticDomReader(scope, 'explicit').project(element);
}

/**
 * What a pick brings along so it is read in its place: the nearest labelled
 * places around it, and the first of what it contains, within a size that
 * leaves the rest of the view its room.
 */
const PICK_CONTEXT = { places: 4, contained: 8, containedBytes: 768 };
const PICK_PATH_SEPARATOR = ' \u203a ';

/** The labelled places around a picked element up to its picking root, outermost first. */
function pickPlaces(element: Element, root: HTMLElement): string[] {
  const places: string[] = [];
  for (
    let current = element === root ? null : parent(element), depth = 0;
    current && depth < 128 && places.length < PICK_CONTEXT.places;
    current = current === root ? null : parent(current), depth++
  ) {
    const label = attributePickLabel(current).slice(0, PICK_LABEL.length);
    // A wrapper that repeats the name of what it wraps is no place of its own.
    if (label && label !== places[0]) places.unshift(label);
  }
  return places;
}

/**
 * Read-side projection of an explicit pick with what surrounds it: the picked
 * element first, naming the places it is inside of, then what it contains.
 * Null when the element is no longer read.
 */
export function projectSemanticPick(
  element: Element,
  root: HTMLElement,
  scope: WorkspaceViewScope
): UiSemanticElement[] | null {
  const [own, ...contained] = new SemanticDomReader(scope, 'explicit').subtree(
    element
  );
  if (!own) return null;
  const places = pickPlaces(element, root);
  const within = (path: readonly string[]) =>
    path.join(PICK_PATH_SEPARATOR).slice(0, UI_ELEMENT_WITHIN_MAX_LENGTH);
  const result: UiSemanticElement[] = [
    { ...own, ...(places.length ? { within: within(places) } : {}) },
  ];
  const inside = within([...places, own.label || own.role]);
  let bytes = 0;
  for (const entry of contained.slice(0, PICK_CONTEXT.contained)) {
    const item = { ...entry, within: inside };
    bytes += new TextEncoder().encode(JSON.stringify(item)).length;
    if (bytes > PICK_CONTEXT.containedBytes) break;
    result.push(item);
  }
  return result;
}

/** The existing local shadow observers are also fed by the admitted request walk. */
export type SemanticDomObservation = (() => void) & {
  observeShadow(shadow: ShadowRoot): void;
};

/** Mutations/events inspect bounded local targets only; request reads own traversal. */
export function observeSemanticDom(
  root: HTMLElement,
  onChange: () => void,
  isCurrent: () => boolean = () => root.isConnected
): SemanticDomObservation {
  const window = root.ownerDocument.defaultView;
  if (!window) return Object.assign(() => {}, { observeShadow: () => {} });
  let disposed = false,
    frame: number | null = null,
    dirty = false,
    recordVisits = 0,
    nodeVisits = 0,
    ancestorVisits = 0;
  const ignoredSelector =
    '[data-copilot-ignore],[data-ai-cursor],[data-sensitive],[data-secret],[contenteditable]:not([contenteditable="false" i]),[role="textbox"],iframe,canvas,input,textarea,select,script,style';
  const ignoredElement = (element: Element) =>
    element.matches(ignoredSelector) ||
    /ai-chat|^chat-/iu.test(element.localName);
  const current = () => !disposed && root.isConnected && isCurrent();
  // No cache survives a callback: changed ignore/ancestor attributes are fresh.
  // The shared frame budget bounds even many independent observer callbacks.
  const classifyBatch = () => {
    const cache = new WeakMap<Node, { within: boolean; ignored: boolean }>();
    return (node: Node): { within: boolean; ignored: boolean } => {
      const chain: Node[] = [];
      let cursor: Node | null = node;
      let admission = { within: false, ignored: false };
      while (
        cursor &&
        chain.length < DOM_LIMIT.depth &&
        ancestorVisits < 1024
      ) {
        const retained = cache.get(cursor);
        if (retained) {
          admission = retained;
          break;
        }
        ancestorVisits++;
        chain.push(cursor);
        if (cursor === root) {
          admission.within = true;
          break;
        }
        cursor = cursor instanceof ShadowRoot ? cursor.host : cursor.parentNode;
      }
      for (let index = chain.length - 1; index >= 0; index--) {
        const item = chain[index];
        admission = {
          within: admission.within,
          ignored:
            admission.ignored ||
            (item instanceof Element && ignoredElement(item)),
        };
        cache.set(item, admission);
      }
      return admission;
    };
  };
  const schedule = (relevant: boolean) => {
    if (!current()) return;
    dirty ||= relevant;
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      const notify = dirty;
      dirty = false;
      recordVisits = nodeVisits = ancestorVisits = 0;
      if (notify && current()) onChange();
    });
  };
  const options: MutationObserverInit = {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      'aria-label',
      'aria-labelledby',
      'aria-selected',
      'aria-checked',
      'aria-current',
      'aria-disabled',
      'aria-hidden',
      'role',
      'title',
      'disabled',
      'hidden',
      'inert',
      'open',
      'class',
      'style',
      'data-state',
      'data-active',
      'data-disabled',
      'data-sensitive',
      'data-secret',
      'contenteditable',
    ],
  };
  const shadows = new Map<ShadowRoot, MutationObserver>();
  const retire = (classify: ReturnType<typeof classifyBatch>) => {
    // At most sixteen retained hosts, never the removed subtree's descendants.
    for (const [shadow, observer] of shadows) {
      if (ancestorVisits >= 1024) break;
      const admission = classify(shadow.host);
      if (ancestorVisits >= 1024) break;
      if (!shadow.host.isConnected || !admission.within || admission.ignored) {
        observer.disconnect();
        shadows.delete(shadow);
      }
    }
  };
  const observeShadow = (shadow: ShadowRoot) => {
    if (!current() || shadows.has(shadow)) return;
    schedule(false);
    const classify = classifyBatch();
    retire(classify);
    if (shadows.size >= 16) return;
    const admission = classify(shadow.host);
    if (!admission.within || admission.ignored) return;
    const observer = new window.MutationObserver(records);
    shadows.set(shadow, observer);
    observer.observe(shadow, options);
    // Registration never walks shadow children. The existing bounded request
    // collector supplies nested roots when it actually admits them.
  };
  function records(batch: MutationRecord[]) {
    if (!current()) return;
    schedule(false); // Reset work counters even for wholly ignored bursts.
    if (dirty) return;
    const classify = classifyBatch();
    retire(classify);
    let relevant = false;
    for (let index = 0; index < batch.length && recordVisits < 64; index++) {
      recordVisits++;
      const record = batch[index];
      const admission = classify(record.target);
      if (!admission.within || admission.ignored) continue;
      if (record.type === 'childList') {
        for (const nodes of [record.addedNodes, record.removedNodes]) {
          for (
            let index = 0;
            index < nodes.length && nodeVisits < 64;
            index++
          ) {
            nodeVisits++;
            const node = nodes[index];
            if (
              node.nodeType === Node.COMMENT_NODE ||
              (node instanceof Element && ignoredElement(node))
            )
              continue;
            relevant = true;
            if (node instanceof Element && node.isConnected && node.shadowRoot)
              observeShadow(node.shadowRoot);
          }
        }
      } else relevant = true;
      if (record.target instanceof Element && record.target.shadowRoot)
        observeShadow(record.target.shadowRoot);
    }
    if (relevant) schedule(true);
  }
  const observer = new window.MutationObserver(records);
  observer.observe(root, options);
  // Mount-only admission is bounded before firstChild/nextSibling access.
  // No mutation, scroll or resize path calls this discovery pass.
  const stack: Array<{ node: Node; depth: number; sibling: boolean }> = [
    { node: root, depth: 0, sibling: false },
  ];
  let visits = 0;
  while (current() && stack.length && visits < DOM_LIMIT.nodes) {
    const { node, depth, sibling } = stack.pop()!;
    if (depth >= DOM_LIMIT.depth) continue;
    visits++;
    if (sibling && visits < DOM_LIMIT.nodes) {
      const next = node.nextSibling;
      if (next) stack.push({ node: next, depth, sibling: true });
    }
    if (!(node instanceof Element) || ignoredElement(node)) continue;
    const shadow = node.shadowRoot;
    if (shadow) observeShadow(shadow);
    if (depth + 1 >= DOM_LIMIT.depth || visits >= DOM_LIMIT.nodes) continue;
    const shadowChild = shadow?.firstChild;
    if (shadowChild)
      stack.push({ node: shadowChild, depth: depth + 1, sibling: true });
    const first = node.firstChild;
    if (first) stack.push({ node: first, depth: depth + 1, sibling: true });
  }
  ancestorVisits = 0;
  const event = (event: Event) => {
    if (!current()) return;
    schedule(false);
    const target = event.composedPath()[0];
    if (target instanceof Node) {
      const admission = classifyBatch()(target);
      if (admission.within && !admission.ignored) schedule(true);
    }
  };
  const selection = () => {
    if (!current()) return;
    schedule(false);
    const anchor = root.ownerDocument.getSelection()?.anchorNode;
    if (anchor) {
      const admission = classifyBatch()(anchor);
      if (admission.within && !admission.ignored) schedule(true);
    }
  };
  const resize = () => schedule(true);
  for (const type of ['focusin', 'focusout', 'scroll'])
    root.addEventListener(type, event, true);
  root.ownerDocument.addEventListener('selectionchange', selection);
  window.addEventListener('resize', resize);
  return Object.assign(
    () => {
      if (disposed) return;
      disposed = true;
      observer.disconnect();
      for (const observer of shadows.values()) observer.disconnect();
      shadows.clear();
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      for (const type of ['focusin', 'focusout', 'scroll'])
        root.removeEventListener(type, event, true);
      root.ownerDocument.removeEventListener('selectionchange', selection);
      window.removeEventListener('resize', resize);
    },
    { observeShadow }
  );
}
